import d3 from 'd3';
import moment from 'moment';
import angular from 'angular';

export default function () {
    return {
        restrict: 'E',
        replace: true,
        controller: vehicleChartController,
        controllerAs: 'chartCtrl',
        bindToController: {
            mileages: '=',
            keepers: '=',
            previousVrms: '=',
            discrepancies: '='
        }
    };
}

function vehicleChartController($scope, $element, $window) {
    const vm = this;
    const chartContainer = $element[0].parentElement;

    let canvasWidth = 0;
    let canvasHeight = 0;
    let x = null;
    let svg = null;
    let settings = null;

    $scope.$watch(watchAggregator, onDataChanged, true);
    angular.element($window).on('resize', onDataChanged);
    $scope.$on('$destroy', () => {
        angular.element($window).off('resize', onDataChanged);
    });

    function drawChart() {
        canvasWidth = chartContainer.clientWidth;
        canvasHeight = chartContainer.clientHeight;
        settings = createSettings();

        // Create the canvas
        const d3Element = d3.select($element[0]);
        if (svg) {
            svg.remove();
        }
        svg = d3Element.append('svg:svg')
        .attr('width', canvasWidth)
        .attr('height', canvasHeight)
        .attr('id', 'vehicle-chart-svg')
        .style('background-color', '#eee');

        const data = formatData();

        // Set up the x axis
        x = getXDomain(data.allDates);

        drawXAxis(x);
        drawKeepers(data, x);
        drawMileageChart(data);
        drawPlateChanges(data, x);
        // drawSpeedoChanges(data, x);
    }

    function watchAggregator() {
        return {
            mileages: vm.mileages,
            keepers: vm.keepers,
            previousVrms: vm.previousVrms,
            discrepancies: vm.discrepancies
        };
    }

    function onDataChanged() {
        drawChart();
    }

    function createSettings() {
        const settings = {};
        settings.canvasMargin = {
            top: 10, right: 10, bottom: 10, left: 55
        };
        settings.mileageChartMargin = {
            top: 10, bottom: 60
        };

        settings.markerLine = {
            top: settings.canvasMargin.top + 10,
            bottom: canvasHeight - settings.canvasMargin.bottom - settings.mileageChartMargin.bottom
        };

        settings.markerText = {
            y: settings.markerLine.top - 5,
            xOffset: -4
        };

        settings.keepers = {
            keeperBarColour: 'blue'
        };

        settings.keepers.bottomBar = {
            textVerticalOffset: -10,
            topAdjustment: 30 // This is sort of magic
        };

        settings.text = {
            fontFamily: 'verdana'
        };
        return settings;
    }

    function drawKeepers(data, x) {
        const rectYPos = settings.canvasMargin.top + settings.mileageChartMargin.top; // Being inconsistent here :(
        const height = canvasHeight - rectYPos - settings.canvasMargin.bottom;
        const textYPos = rectYPos + height + settings.keepers.bottomBar.textVerticalOffset;

        // This needs to be before the group to make sure the ordering is right
        // Adds a keeper bar at the bottom. This is literally just a blue bar, nothing special
        // It is the same colour as the main keeper bars, but the opacity causes it to be distinct. Feel free to change it.
        svg.append('rect')
            .attr('x', settings.canvasMargin.left)
            .attr('y', rectYPos + height - settings.mileageChartMargin.bottom + settings.keepers.bottomBar.topAdjustment)
            .attr('width', canvasWidth - settings.canvasMargin.left - settings.canvasMargin.right)
            .attr('height', settings.mileageChartMargin.bottom - settings.keepers.bottomBar.topAdjustment)
            .style('fill', settings.keepers.keeperBarColour)
            .style('opacity', '0.5');

        // Add an SVG group we can stick the actual bars in
        const keeperGroup = svg.append('g'); // If we want to be able to update data, need to move this up so it doesn;t get called again

        const keeperGroups = keeperGroup.selectAll('rect')
            .data(data.keepers)
            .enter()
            .append('g');

        keeperGroups.append('rect')
            .attr('x', d => x(d.start))
            .attr('width', d => x(d.drawEnd) - x(d.start))
            .attr('y', rectYPos)
            .attr('height', height)
            // .attr('stroke', 'black')
            // .attr('stroke-width', 1)
            .style('fill', settings.keepers.keeperBarColour)
            .style('opacity', (d, index) => {
                return index % 2 === 0 ? 0.2 : 0.3;  // This will make the bars alternate in opacity so you can distinguish
            });

        keeperGroups.append('text')
            .attr('x', d => {
                const start = x(d.start);
                const end = x(d.drawEnd);
                if (end < start) {
                    return start; // Shouldnt happen, but...
                }
                return start + (end - start) / 2;
            })
            .attr('y', textYPos)
            .style('font-family', settings.text.fontFamily)
            .style('font-size', '12')
            .style('fill', 'white')
            .text(d => d.keeperNumber);
        // Add a transpsrent rectangle over the text to allow us to capture 'hover'
        // Arguably we could use this instead of the 'general' menu bar - i.e. make this not transparent.

        keeperGroups.append('rect')
            .attr('x', d => x(d.start))
            .attr('y', rectYPos + height - settings.mileageChartMargin.bottom + settings.keepers.bottomBar.topAdjustment)
            .attr('height', settings.mileageChartMargin.bottom - settings.keepers.bottomBar.topAdjustment)
            .attr('width', d => x(d.drawEnd) - x(d.start))
            .style('opacity', '0')
            .append('svg:title')  // This appends a 'title' element to the element, which is what will show in a mouse hover. You could replace it with some fancy jQuery if you wanted
            .text(d => {
                let label = `${d.name} - ${moment(d.start).format('MMM YYYY')}`;
                if (d.displayEnd) {
                    label = `${label} to ${moment(d.displayEnd).format('MMM YYYY')}`;
                }
                return label;
            });
    }

    function drawPlateChanges(data, x) {
        // Add one SVG group to put all the plate changes in
        const pcGroup = svg.append('g');

        // Create one SVG Group for each plate change
        const pcGroups = pcGroup.selectAll('line')
            .data(data.plateChanges)
            .enter()
            .append('g');

        // For each plate change group, add the line and then the text for that plate change
        pcGroups.append('line')
            .attr('x1', d => x(d.date))
            .attr('x2', d => x(d.date))
            .attr('y1', settings.markerLine.top)
            .attr('y2', settings.markerLine.bottom)
            .attr('stroke', '#777')
            .attr('stroke-width', 3)
            .attr('stroke-dasharray', '5,5');

        pcGroups.append('text')
            .attr('x', d => x(d.date) + settings.markerText.xOffset)
            .attr('y', settings.markerText.y)
            .text('P')
            .style('font-family', settings.text.fontFamily) // could probably move all this to CSS
            .style('font-size', 15)
            .append('svg:title') // Add a 'hover' element
            .text(d => `New plate '${d.newPlate}' on ${formatDateForDisplay(d.date)}`);
    }

    // Could be refactored to share code with the plate changes I guess...
    // function drawSpeedoChanges(data, x) {
    //     var pcGroup = svg.append('g');

    //     var pcGroups = pcGroup.selectAll('line')
    //         .data(data.speedoChanges)
    //         .enter()
    //         .append('g');

    //     pcGroups.append('line')
    //         .attr('x1', function(d) { return x(d.date); })
    //         .attr('x2', function(d) { return x(d.date); })
    //         .attr('y1', settings.markerLine.top)
    //         .attr('y2', settings.markerLine.bottom)
    //         .attr('stroke', '#777')
    //         .attr('stroke-width', 3)
    //         .attr('stroke-dasharray', '5,5');

    //     pcGroups.append('text')
    //         .attr('x', function(d) { return x(d.date) + settings.markerText.xOffset; })
    //         .attr('y', settings.markerText.y)
    //         .text('S')
    //         .style('font-family', settings.text.fontFamily) // could probably move all this to CSS
    //         .style('font-size', 15)
    //         .append('svg:title') //Add a 'hover' element
    //         .text(function(d) { return 'Speedo changed on ' + formatDateForDisplay(d.date);});
    // }

    function drawXAxis(x) {
        const xAxis = d3.svg.axis()
            .scale(x)
            .orient('bottom');
        svg.append('g')
            .attr('class', 'axis')
            .attr('transform', `translate(0,${(canvasHeight - settings.canvasMargin.bottom - settings.mileageChartMargin.bottom)})`)
            .call(xAxis);
    }

    function drawYAxis(y) {
        const yAxis = d3.svg.axis()
            .scale(y)
            .orient('left');

        svg.append('g')
            .attr('class', 'axis')
            .attr('transform', `translate(${(settings.canvasMargin.left - 5)},0)`)
            .call(yAxis);
    }

    function drawMileageChart(data) {
        const mileageFunction = d => d.mileage;
        // SVG has 0 at the top so this scale function reverses it for us to make life easy
        const bottomMostY = canvasHeight - settings.canvasMargin.bottom - settings.mileageChartMargin.bottom;
        const topMostY = settings.canvasMargin.top + settings.mileageChartMargin.top;
        const domain = d3.extent(data.allMileages, mileageFunction);
        const y = d3.scale.linear()
            .range([bottomMostY, topMostY])
            .domain([0, domain[1]]);

        drawYAxis(y);

        const mileageChart = svg.append('g');
        // Note: rendering the chart as individual line segments, rather than a path, in order to colour segments differently
        mileageChart.selectAll('line')
            .data(data.includedMileages.filter(m => m.previousDate))
            .enter()
            .append('line')
            .attr('x1', d => x(d.previousDate))
            .attr('x2', d => x(d.date))
            .attr('y1', y(0))
            .attr('y2', y(0))
            .attr('stroke', d => d.previousMileage <= d.mileage ? 'green' : 'red')  // Consider adding more rules here, showing some amber for suspect mileages
            .attr('stroke-width', 2)
            .transition()
            .attr('y1', d => y(d.previousMileage))
            .attr('y2', d => y(d.mileage));

        // Render the ignored mileages
        renderMileageCircles(data.ignoredMileages, x, y, 'grey');
        renderMileageCircles(data.includedMileages, x, y, 'blue');
    }

    function renderMileageCircles(mileages, xscale, yscale, colour) {
        const group = svg.append('g');  // This means you can't really refresh the data easily withut redrawing the whole thing
        const circles = group.selectAll('circle')
            .data(mileages)
            .enter()
            .append('svg:circle')
            .attr('r', 4)
            .attr('cx', d => xscale(d.date))
            .attr('cy', () => yscale(0))
            .attr('fill', colour);  // Note: we could be clever about this colour too, if we wanted.

        // Use transition to move the mileages to their home
        // If you don't want to use transitions, you can get rid of this and append the hover text directly to the circles and get rid of the independent var
        circles
            .transition()
            .attr('cy', d => yscale(d.mileage));

        // Append a hover text to the mileages
        circles
        .append('svg:title')
        .text(d => `${formatMileageForDisplay(d.mileage)}  on ${formatDateForDisplay(d.date)} from ${d.source}`);
    }

    // This provides a mapping function that maps all our dates on to a pixel value that fits within the canvas and the margins we have specified
    function getXDomain(allDates) {
        return d3.time.scale()
        .range([settings.canvasMargin.left, canvasWidth - settings.canvasMargin.right])
        .domain(d3.extent(allDates));
    }

    function formatDateForDisplay(d) {
        return moment(d).format('D MMM YYYY');
    }

    function formatMileageForDisplay(m) {
        return `${m.toLocaleString()} miles`; // NOTE: You probably want a nicer format than this! And maybe error handling :)
    }

    function formatData() {
        const result = {};

        const mileageSorter = (a, b) => {
            if (a.date < b.date) {
                return -1;
            }
            if (a.date > b.date) {
                return 1;
            }
            if (a.mileage < b.mileage) {
                return -1;
            }
            if (a.mileage > b.mileage) {
                return 1;
            }
            return 0;
        };

        const keeperSorter = (a, b) => {
            if (a.start < b.start) {
                return -1;
            }
            if (a.start > b.start) {
                return 1;
            }
            return 0;
        };

        result.allMileages = vm.mileages.map(item => {
            return {
                date: parseDate(item.dateRecorded),
                mileage: item.mileage,
                ignore: item.excluded,
                source: item.source
            };
        });
        result.allMileages = result.allMileages.sort(mileageSorter);
        result.includedMileages = result.allMileages.filter(d => !d.ignore);
        result.ignoredMileages = result.allMileages.filter(d => d.ignore);

        result.includedMileages.forEach((item, index, theArray) => {
            if (index > 0) {
                const previousItem = theArray[index - 1];
                item.previousDate = previousItem.date;
                item.previousMileage = previousItem.mileage;
            }
        });

        result.keepers = vm.keepers.map(item => {
            return {
                name: item.name || `Keeper ${item.keeperPosition}`,
                start: parseDate(item.dateChanged),
                keeperNumber: item.keeperPosition // This can actually be null
            };
        });

        result.keepers = result.keepers.sort(keeperSorter);

        result.keepers.forEach((item, index, theArray) => {
            if (index < theArray.length - 1) {
                const nextItem = theArray[index + 1];
                if (nextItem.start) {
                    // http://stackoverflow.com/questions/563406/add-days-to-datetime
                    const endDate = new Date(nextItem.start);
                    endDate.setDate(endDate.getDate() - 1);
                    item.drawEnd = endDate;
                    item.displayEnd = endDate;
                }
            }
            if (index === theArray.length - 1) {
                item.drawEnd = new Date();
            }
        });

        result.plateChanges = vm.previousVrms.map(item => {
            return {
                newPlate: item.vrm,
                // date: dateFormat.parse(item.date)
                date: parseDate(item.date)
            };
        });

        // Need all dates to be able to figure out the x axis
        result.allDates = result.allMileages.map(m => m.date)
                            .concat(result.keepers.map(k => k.start))
                            .concat(result.keepers.map(k => k.drawEnd))
                            // .concat(result.speedoChanges.map(function(s) { return s.date; }))
                            .concat(result.plateChanges.map(p => p.date));

        return result;
    }

    function parseDate(date) {
        // We get inconsistent date formats from the server, so can't reliably use D3's parser

        if (!date) {
            return null;
        }

        // Because these are coming from the server in UTC, we will actually add an hour to dates in the summer
        // However, as the data is just supposed to be dates, it shouldn't matter (famous last words...)
        return new Date(date);
    }
}



// WEBPACK FOOTER //
// ./Client/app/tasks/directives/vehicleChart.js